import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { AGENT_MODES, getAgentMode } from '../config/agentModes';
import '../../shared/components/custom/CustomComponents.css';

const DARK_DROPDOWN_STYLE = {
  background:
    'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
  border: '1px solid rgba(96, 165, 250, 0.6)',
  backdropFilter: 'blur(12px)',
  boxShadow:
    '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)',
};

const LIGHT_DROPDOWN_STYLE = {
  background: '#ffffff',
  border: '1px solid rgb(226 232 240)',
  boxShadow:
    '0 20px 25px -5px rgb(0 0 0 / 0.08), 0 8px 10px -6px rgb(0 0 0 / 0.08)',
};

export default function AIAgentModePicker({
  value,
  onChange,
  lightSurface = false,
  disabled = false,
}) {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.shared.aiAgent.modePicker.${key}`, opts);
  const { isDashboardLightMode } = useGlobalTheme();
  const isLight = lightSurface || isDashboardLightMode;
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0, openUp: true });
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const activeMode = getAgentMode(value);
  const ActiveIcon = activeMode.icon;

  const options = useMemo(
    () =>
      AGENT_MODES.map((mode) => ({
        value: mode.id,
        label: mode.label,
        icon: mode.icon,
        description: mode.description,
      })),
    [],
  );

  useEffect(() => {
    if (!open || !rootRef.current) return undefined;

    const updatePosition = () => {
      const rect = rootRef.current.getBoundingClientRect();
      const menuHeight = 168;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < menuHeight + 12 && rect.top > menuHeight + 12;

      setMenuPos({
        top: openUp ? rect.top + window.scrollY - 6 : rect.bottom + window.scrollY + 6,
        left: rect.left + window.scrollX,
        width: 112,
        openUp,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      const inRoot = rootRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inRoot && !inMenu) setOpen(false);
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const dropdownStyle = {
    position: 'absolute',
    left: menuPos.left,
    width: menuPos.width,
    zIndex: 9999999,
    ...(isLight ? LIGHT_DROPDOWN_STYLE : DARK_DROPDOWN_STYLE),
    ...(menuPos.openUp
      ? { top: menuPos.top, transform: 'translateY(-100%)' }
      : { top: menuPos.top }),
  };

  return (
    <div ref={rootRef} className="relative inline-flex shrink-0">
      <button
        type="button"
        onClick={() => !disabled && setOpen((prev) => !prev)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={p('mode_aria', { mode: activeMode.label })}
        title={activeMode.description}
        className={`
          rounded-lg px-2 py-1 text-[0.62rem] font-semibold
          focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all
          inline-flex items-center gap-1
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${
            isLight
              ? 'border border-slate-300 bg-white text-slate-900 hover:bg-slate-50'
              : 'border border-white/20 bg-white/10 text-white hover:bg-blue-500/10'
          }
        `}
      >
        <ActiveIcon size={11} className="shrink-0" />
        <span className="whitespace-nowrap">{activeMode.label}</span>
        <ChevronDown
          size={11}
          className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''} ${
            isLight ? 'text-slate-500' : 'text-white/80'
          }`}
        />
      </button>

      {open &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            role="listbox"
            aria-label={p('modes_aria')}
            className={`rounded-xl overflow-hidden ${isLight ? 'ring-1 ring-slate-200/80' : 'shadow-2xl'}`}
            style={dropdownStyle}
          >
            <div className="p-1 custom-scrollbar">
              {options.map((option) => {
                const Icon = option.icon;
                const isSelected = option.value === value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    title={option.description}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                    className={`w-full text-left px-2 py-1.5 text-[0.68rem] font-medium rounded-md flex items-center gap-1.5 ${
                      isLight
                        ? `transition-colors text-slate-800 hover:bg-slate-100 ${
                            isSelected ? 'font-semibold bg-blue-50 text-blue-900' : ''
                          }`
                        : `transition-colors text-white/90 hover:bg-blue-500/20 ${
                            isSelected ? 'font-semibold bg-blue-500/15 text-white' : ''
                          }`
                    }`}
                  >
                    <Icon size={12} className="shrink-0 opacity-90" />
                    <span className="flex-1 truncate">{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
